import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
const NotificationContext = createContext(undefined);
export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};
export const NotificationProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [notification, setNotification] = useState({
        message: '',
        severity: 'info',
        autoHideDuration: 6000,
    });
    // Use a ref to track the last notification to prevent duplicate notifications
    const lastNotificationRef = useRef('');
    // Use a ref to track the timeout ID for auto-dismissal
    const timeoutRef = useRef(null);
    // Use useCallback to memoize the function and prevent it from changing on every render
    const showNotification = useCallback((newNotification) => {
        // Only show the notification if it's different from the last one
        const notificationKey = `${newNotification.message}-${newNotification.severity}`;
        if (notificationKey !== lastNotificationRef.current) {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            lastNotificationRef.current = notificationKey;
            setNotification(newNotification);
            setOpen(true);
            // Set up auto-dismiss timeout if autoHideDuration is provided
            if (newNotification.autoHideDuration) {
                timeoutRef.current = setTimeout(() => {
                    setOpen(false);
                }, newNotification.autoHideDuration);
            }
        }
    }, []);
    const hideNotification = useCallback(() => {
        setOpen(false);
        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);
    const handleClose = useCallback((_event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        hideNotification();
    }, [hideNotification]);
    // Clean up timeouts when the component unmounts
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    return (_jsxs(NotificationContext.Provider, { value: { showNotification, hideNotification }, children: [children, _jsx(Snackbar, { open: open, autoHideDuration: notification.autoHideDuration || 6000, onClose: handleClose, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, children: _jsx(Alert, { onClose: handleClose, severity: notification.severity, variant: "filled", children: notification.message }) })] }));
};
